import React, {useCallback, useMemo, useState} from "react";
import {Box, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import {useTranslation} from "react-i18next";
import Dialog from "../../base/dialog";
import Button from "../../base/button/button";
import Form, {MuiRFF, Yup} from "../../form";
import WarehouseAutocomplete from "../../text-fields/autocomplete/warehouse-autocomplete";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import Utils from "../../../../core/services/utils/utils";

const formNames = {
    warehouse: 'warehouse',
};

/**
 * Maps a warehouse record to the autocomplete option shape.
 */
const mapWarehouseToOption = (warehouse) => {
    if (!warehouse) {
        return null;
    }
    if (typeof warehouse === 'string') {
        return {id: warehouse, code: warehouse, name: warehouse};
    }
    return {
        id: warehouse.id ?? warehouse.code,
        code: warehouse.code ?? warehouse.name,
        name: warehouse.name ?? warehouse.code,
    };
};

/**
 * Dialog for updating purchase-order properties (currently warehouse selection).
 * Loads warehouses the operator has access to via WarehouseAutocomplete / getOperatorWarehouses.
 */
const UpdatePurchaseOrderPropertiesDialog = ({
                                                 open = false,
                                                 close,
                                                 entityToShow = 'warehouse',
                                                 warehouse = null,
                                                 update,
                                             }) => {
    const {t} = useTranslation('dialogs', {keyPrefix: 'app-specific.update-purchase-order-properties'});
    const [submitting, setSubmitting] = useState(false);

    const schema = useMemo(() => Yup.object().shape({
        [formNames.warehouse]: Yup.mixed().nullable().required(ValidateMessages.required),
    }), []);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        [formNames.warehouse]: entityToShow === 'warehouse' ? mapWarehouseToOption(warehouse) : null,
    }), [entityToShow, warehouse]);

    const formKey = useMemo(
        () => `update-po-props-${open}-${entityToShow}-${warehouse?.code ?? warehouse?.id ?? ''}`,
        [entityToShow, open, warehouse],
    );

    const dialogTitle = useMemo(() => {
        switch (entityToShow) {
            case 'warehouse':
            default:
                return t('title.warehouse');
        }
    }, [entityToShow, t]);

    /**
     * Saves the selected warehouse property (shell until live update API).
     */
    const submitForm = useCallback(async (values) => {
        if (entityToShow !== 'warehouse' || !values?.[formNames.warehouse]) {
            return;
        }
        setSubmitting(true);
        // TODO: live UpdatePurchaseOrderProperties API
        await Utils.wait(200);
        update?.({
            warehouse: mapWarehouseToOption(values[formNames.warehouse]),
        });
        setSubmitting(false);
        close?.();
    }, [close, entityToShow, update]);

    return (
        <Dialog open={open} setOpen={close} maxWidth={'sm'}>
            <DialogTitle>
                <p className={'text-lg text-color-1 font-weight-semi-bold text-center'}>
                    {dialogTitle}
                </p>
            </DialogTitle>
            <DialogContent dividers>
                <Form
                    key={formKey}
                    initialValues={initialValues}
                    onSubmit={submitForm}
                    validate={validate}
                    formProps={{id: 'update-purchase-order-properties-form'}}
                >
                    <Grid2 spacing={2} container>
                        {
                            entityToShow === 'warehouse' &&
                            <Grid2 xs={12}>
                                <WarehouseAutocomplete
                                    form
                                    name={formNames.warehouse}
                                    label={t('fields.warehouse')}
                                    required={required[formNames.warehouse]}
                                    disabled={submitting}
                                    textFieldProps={{
                                        size: 'small',
                                    }}
                                />
                            </Grid2>
                        }
                    </Grid2>
                </Form>
            </DialogContent>
            <DialogActions className={'p-0'}>
                <Box
                    sx={{
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                    }}
                >
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        disabled={submitting}
                        onClick={() => close?.()}
                        borderRadius={'0 8px 0 8px'}
                    >
                        {t('actions.close')}
                    </Button>
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        type={'submit'}
                        form={'update-purchase-order-properties-form'}
                        disabled={submitting}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {submitting ? t('actions.saving') : t('actions.save')}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default UpdatePurchaseOrderPropertiesDialog;
